import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Presurvey from "./ui/Presurvey";
import ParentConsentPopup from "./ParentConsentPopup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

const logo = process.env.PUBLIC_URL + "/images/logo.png";

const ParentSignup = ({ handleSignup }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    email: "",
    firstName: "",
    lastName: "",
    relationship: "",
    address: "",
    s1: "",
    s2: "",
    s3: "",
    s4: "",
    s5: "",
    s6: "",
  });

  const [error, setError] = useState("");
  const [termsAndConditionCheck, setTermsAndConditionCheck] = useState(false);
  const [isConsentPopupVisible, setIsConsentPopupVisible] = useState(false);
  const [preSurvey, setPreSurvey] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);

  // Payload tracking variables for backend legal parameters
  const [parentConsentScope, setParentConsentScope] = useState([]);
  const [verificationRefToken, setVerificationRefToken] = useState("");

  const handleTermsCheckboxToggle = () => {
    const isValidPhone = /^\d{10}$/.test(formData.phoneNumber);
    if (!isValidPhone) {
      return toast.warn("Please enter a valid 10-digit phone number first before verification.");
    }

    if (termsAndConditionCheck) {
      setTermsAndConditionCheck(false);
      setVerificationRefToken("");
      setParentConsentScope([]);
    } else {
      setIsConsentPopupVisible(true);
    }
  };

  const handleVerificationWorkflowSuccess = (payload) => {
    setParentConsentScope(payload.consentScope);
    setVerificationRefToken(payload.transactionReceipt);
    setFormData((prev) => ({
      ...prev,
      aadhaar_number: payload.aadhaar_number,
      age: payload.age,
      version: payload.consent_form_version,
      required_consent_scope: payload.required_consent_scope,
    }));
    setIsConsentPopupVisible(false);
    setTermsAndConditionCheck(true);
    setPreSurvey(true); 
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phoneNumber") {
      const numericVal = value.replace(/\D/g, "");
      if (value !== numericVal) {
        toast.warn("Please enter only numbers");
      }
      setFormData((prev) => ({ ...prev, [name]: numericVal }));
      return;
    }
    if (name === "address" || name === "firstName") {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    } else if (name === "email") {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value.toLowerCase().trim(),
      }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value.trim() }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nonRequiredFields = ["email", "s1", "s2", "s4"];
    const requiredFields = Object.keys(formData).filter(
      (field) => !nonRequiredFields.includes(field)
    );

    if (requiredFields.some((field) => !formData[field] || (typeof formData[field] === "string" && formData[field].trim() === ""))) {
      toast.warn("Please fill all required fields.");
      return;
    }

    if (formData.email && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      return toast.warn("Please provide a valid email address");
    }

    if (!/^\d{10}$/.test(formData.phoneNumber)) {
      return toast.warn("Please enter 10 digit phone number");
    }

    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_]).{8,16}$/;
    if (!passwordRegex.test(formData.password)) {
      toast.warn("Password must be 8-16 characters long and include uppercase, lowercase, number, and special character.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.warn("Passwords do not match");
      return;
    }

    if (!termsAndConditionCheck || !verificationRefToken) {
      toast.warn("Identity verification is required to complete signup.");
      return;
    }

    try {
      const transformedData = {
        password: formData.password,
        phone_number: formData.phoneNumber,
        email: formData.email,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName,
        relationship: formData.relationship,
        address: formData.address.trim(),
        user_type: "parent",
        s1: formData.s1,
        s2: formData.s2,
        s3: formData.s3,
        s4: formData.s4,
        s5: formData.s6,
        s6: formData.s6,
        // Injected parameters passed along during account creation hook
        aadhaar_number: formData.aadhaar_number,
        request_id: verificationRefToken.trim(),
        age: formData.age || null,
        required_consent_scope: formData.required_consent_scope || false,
        optional_consent_scope: parentConsentScope,
        consent_mechanism: "Aadhar OTP",
        consent_form_version: formData.version,
      };

      const response = await handleSignup(transformedData);

      if (response && response.success) {
        toast.success("Signup Successful! Proceeding to login.");
        navigate("/parent-login");
      } else {
        return toast.error(response.message || "Could not execute registration request.");
      }
    } catch (error) {
      setError("Signup failed. Please try again.");
    }
  };

  return (
    <div className="signup-container">
      {(isConsentPopupVisible || preSurvey) && <div className="term-privacy-overlay"></div>}

      <ParentConsentPopup
        visible={isConsentPopupVisible}
        userPhoneNumber={formData.phoneNumber}
        forexistingParent={false}
        onCancel={() => setIsConsentPopupVisible(false)}
        onSuccess={handleVerificationWorkflowSuccess}
      />

      {preSurvey && (
        <div className="term-privacy-popup" style={{ maxWidth: "800px" }}>
          <Presurvey
            setPreSurvey={setPreSurvey}
            setTermsAndConditionCheck={setTermsAndConditionCheck}
            setFormData={setFormData}
          />
        </div>
      )}

      <div className="card form-container">
        <div className="login-center">
          <img src={logo} alt="Logo" className="logo" />
          <h1>Smile2Steps Sign Up</h1>
          <div className="tabs">
            <p className="tab-left">
              <NavLink to={"/parent-signup"} className={({ isActive }) => (isActive ? "active-tab" : "")}>
                Parent
              </NavLink>
            </p>
            <p className="tab-right">
              <NavLink to={"/pediatrician-signup"} className={({ isActive }) => (isActive ? "active-tab" : "")}>
                Provider
              </NavLink>
            </p>
          </div>
        </div>
        <form noValidate onSubmit={handleSubmit}>
          <div className="grid-container">
            <div className="grid-item">
              <label>Phone Number *</label>
              <input
                type="text"
                name="phoneNumber"
                maxLength={10}
                value={formData.phoneNumber}
                onChange={handleChange}
                autoComplete="phone_number"
              />
            </div>
            <div className="grid-item">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === " ") e.preventDefault();
                }}
                autoComplete="email"
              />
            </div>
            <div className="grid-item">
              <label>First Name *</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                autoComplete="first-name"
              />
            </div>
            <div className="grid-item">
              <label>Last Name *</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                autoComplete="last-name"
              />
            </div>
            <div className="grid-item">
              <label>Relationship with Child *</label>
              <select
                name="relationship"
                value={formData.relationship}
                onChange={handleChange}
                autoComplete="relationship"
                required
              >
                <option disabled value=""></option>
                <option value="Mother">Mother</option>
                <option value="Father">Father</option>
                <option value="Others">Others</option>
              </select>
            </div>
            <div className="grid-item">
              <label>City & State *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                autoComplete="address"
              />
            </div>
            <div style={{ position: "relative", width: "100%" }}>
              <div className="grid-item">
                <label>Password *</label>
                <input
                  type={!isPasswordVisible ? "password" : "text"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
                <FontAwesomeIcon
                  icon={isPasswordVisible ? faEyeSlash : faEye}
                  onClick={() => setIsPasswordVisible((prev) => !prev)}
                  style={{
                    position: "absolute",
                    right: "32px",
                    top: "56%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#555",
                  }}
                />
              </div>
            </div>
            <div style={{ position: "relative", width: "100%" }}>
              <div className="grid-item">
                <label>Confirm Password *</label>
                <input
                  type={!isConfirmPasswordVisible ? "password" : "text"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
                <FontAwesomeIcon
                  icon={isConfirmPasswordVisible ? faEyeSlash : faEye}
                  onClick={() => setIsConfirmPasswordVisible((prev) => !prev)}
                  style={{
                    position: "absolute",
                    right: "32px",
                    top: "56%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#555",
                  }}
                />
              </div>
            </div>
          </div>
          
          <button
            disabled={!termsAndConditionCheck}
            type="submit"
            className={`${termsAndConditionCheck ? "input-btn" : "disablebtn"}`}
          >
            Sign Up
          </button>
          
          <div className="t-and-c-container" onClick={handleTermsCheckboxToggle} style={{ cursor: "pointer" }}>
            <input
              type="checkbox"
              readOnly
              checked={termsAndConditionCheck}
              style={{ pointerEvents: "none" }}
            />
            <div className="t-and-c">
              <span>I agree to the Parental Consent & Identity Verification terms *</span>
            </div>
          </div>
        </form>

        <p className="login-navigation">
          Already have an account? Login (
          <span>
            <Link to="/parent-login">Parent</Link>
          </span>
          <b> / </b>
          <span>
            <Link to={"/pediatrician-login"}>Provider</Link>
          </span>
          )
        </p>
        {error && <p className="error-message">{error}</p>}
      </div>
    </div>
  );
};

export default ParentSignup;